import { useState, useEffect, useCallback, useRef } from 'react';
import { Trash2, ExternalLink, Save } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';
import PropTypes from 'prop-types';
import WebCallVariableModal from './WebCallVariableModal';

import ConfirmDeleteModal from '../child/ConfirmDeleteModal';
import { deleteAssistant, saveAssistantDetail } from '../../store/app/assistantSlice';
import { useSelector } from 'react-redux';
import ReassignOrganizationModal from '../child/ReassignOrganizationModal';
import { getAllOrganisationData } from '../../utils/api/organisationAPI';
import { getGeminiResponse } from '../chatWidget/geminiClient';
import ChatWidget from '../chatWidget/ChatWidget';
import Vapi from '@vapi-ai/web';
import { Phone, PhoneOff } from 'lucide-react';
import { createSession } from '../../utils/api/sessionAPI';
import { chatApi } from '../../utils/api/chatApi';

function RightPanel({ assistantId = null, fromOrg = false }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const activeDetail = useSelector((state) => state.assistants.activeDetail);
  // console.log('Active Detail:', activeDetail?.assistant?.externalAssistantId);
  const updatedDetail = useSelector((state) => state.assistants.updateData);
  const dispatch = useDispatch();
  const userOrgId = useSelector((state) => state?.user?.user?.organizationId);

  const [showReassignModal, setShowReassignModal] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [startChat, setStartChat] = useState(false);
  const [startWebCall, setStartWebCall] = useState(false);
  const vapi = useRef(new Vapi("38442b7b-ee7f-4fd2-bd00-158237cb2963")).current;
  const [callDuration, setCallDuration] = useState(0);
  const [isStartWebCall, setIsStartWebCall] = useState(false);
  const [showVariableModal, setShowVariableModal] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);


  const Spinner = () => (
    <span
      style={{
        width: '16px',
        height: '16px',
        marginRight: '0.5rem',
        border: '2px solid white',
        borderTop: '2px solid transparent',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        display: 'inline-block'
      }}
    />
  );

  useEffect(() => {
    let timer;
    if (startWebCall) {
      timer = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else {
      setCallDuration(0);
    }
    return () => clearInterval(timer);
  }, [startWebCall]);

  const formatDuration = (seconds) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, '0');
    const secs = String(seconds % 60).padStart(2, '0');
    return `${mins}:${secs}`;
  };

  useEffect(() => {
    vapi.on("call-start", () => {
      setStartWebCall(true);
      setIsStartWebCall(false);
    });
    vapi.on("call-end", () => setStartWebCall(false));
    return () => vapi.removeAllListeners();
  }, [vapi]);
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleResize = useCallback(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const orgs = await getAllOrganisationData({ setIsLoading: () => { } });
        setOrganizations(orgs.organizations);
      } catch (error) {
        console.error('Error loading organizations:', error);
      }
    };
    fetchOrgs();
  }, []);

  const handleDelete = () => {
    setShowDeleteModal(!showDeleteModal);
    if (showDeleteModal) setIsDeleting(false);
  };

  const handleConfirmDelete = async () => {
    if (!assistantId) {
      toast.error('Invalid assistant ID');
      return;
    }
    setIsDeleting(true);
    try {
      await dispatch(deleteAssistant({ assistantId })).unwrap();
      toast.success('Assistant deleted successfully!');
      setShowDeleteModal(false);
    } catch (error) {
      toast.error(error?.message || 'Delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSave = async () => {
    try {
      await dispatch(saveAssistantDetail({ assistantId, userOrgId })).unwrap();
      toast.success('Assistant updated successfully!');
    } catch (e) {
      toast.error(e?.message || 'Update failed');
    }
  };


  const startCall = (e) => {
    e.preventDefault();

    const firstMessage = activeDetail?.details?.firstMessage || '';
    const promptMessage = activeDetail?.details?.model?.messages?.[0]?.content || '';

    const extractVars = (text) =>
      (text.match(/{{\s*([\w\d_]+)\s*}}/g) || []).map((v) => v.replace(/{{\s*|\s*}}/g, ''));

    const variables = Array.from(new Set([...extractVars(firstMessage), ...extractVars(promptMessage)]));

    if (variables.length > 0) {
      setShowVariableModal(true);
    } else {
      triggerWebCall({});
    }
  };
  const triggerWebCall = async (variables) => {
    setIsStartWebCall(true);
    try {
      await vapi.start(activeDetail?.assistant?.externalAssistantId, {
        variableValues: variables,
      });
    } catch (e) {
      console.error('Error starting call:', e);
      toast.error('Failed to start call');
      setStartWebCall(false);
      setIsStartWebCall(false);
    }
  };
  const handleVariableSubmit = (filledVariables) => {
    setShowVariableModal(false);
    triggerWebCall(filledVariables);
  };


  const stopCall = () => {
    vapi.stop();
    setStartWebCall(false);
  };

  const onSendMessage = async (messages) => {
    try {
      // Create session if it doesn't exist
      let session = currentSession;
      if (!session) {
        const sessionPayload = {
          name: activeDetail?.assistant?.name || "Chat Session",
          assistantId: assistantId
        };
        
        const sessionResponse = await createSession(sessionPayload);
        session = sessionResponse.session;
        setCurrentSession(session);
      }

      // Get the last user message
      const lastMessage = messages[messages.length - 1];
      
      // Prepare chat payload
      const chatPayload = {
        sessionId: session.id,
        input: lastMessage.content,
        assistantOverrides: {
          model: {
            provider: "google",
            model: "gemini-2.5-flash"
          }
        }
      };

      // Call chat API
      const chatResponse = await chatApi(chatPayload);
      
      // Extract assistant response from the chat response
      const assistantMessage = chatResponse.chat.output[0]?.content || 'No response received';
      
      // Store both user and assistant messages
      const userMessage = {
        role: 'user',
        content: lastMessage.content
      };
      
      const assistantResponse = {
        role: 'assistant',
        content: assistantMessage
      };
      
      // Update chat messages state with both messages
      setChatMessages(prev => {
        // Check if user message already exists to avoid duplicates
        const lastStoredMessage = prev[prev.length - 1];
        if (lastStoredMessage && lastStoredMessage.role === 'user' && lastStoredMessage.content === userMessage.content) {
          // User message already exists, just add assistant message
          return [...prev, assistantResponse];
        } else {
          // Add both user and assistant messages
          return [...prev, userMessage, assistantResponse];
        }
      });
      
      return assistantMessage;
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
      throw error;
    }
  };

  return (
    <div
      style={{
        width: isMobile ? '100%' : 'auto',
        margin: isMobile ? '1rem 0' : '0 auto',
        backgroundColor: 'white',
        borderRadius: '0.75rem',
        border: '1px solid #e5e7eb',
        padding: isMobile ? '1rem 0.75rem' : '1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        boxSizing: 'border-box',
        height: 'auto',
        alignSelf: 'flex-start',
        overflowY: 'scroll',
      }}
    >
      <Link
        to={`/call-logs?assistantId=${assistantId}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '0.5rem 0.75rem',
          border: '1px solid #e5e7eb',
          borderRadius: '0.5rem',
          backgroundColor: 'transparent',
          transition: 'background-color 0.2s',
          color: '#111827',
          textDecoration: 'none',
          fontWeight: '500',
          fontSize: '0.875rem',
        }}
        onMouseEnter={(e) => (e.target.style.backgroundColor = '#f9fafb')}
        onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
      >
        <span>Call logs</span>
        <ExternalLink style={{ width: '0.875rem', height: '0.875rem' }} />
      </Link>

      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          borderBottom: '1px solid #e0e0e0',
          paddingBottom: '10px',
        }}
      >
        <div className='flex-1'>
          <button
            onClick={handleSave}
            type="button"
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.375rem',
              backgroundColor: 'var( --primary-500)',
              color: 'white',
              fontWeight: '500',
              padding: '0.5rem 0.75rem',
              borderRadius: '0.5rem',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              fontSize: '0.875rem',
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = 'var( --primary-600)')}
            onMouseLeave={(e) => (e.target.style.backgroundColor = 'var( --primary-500)')}
          >
            <Save style={{ width: '0.875rem', height: '0.875rem' }} />
            Save
          </button>
        </div>
        <div className='flex-1'>
          <button
            type="button"
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.375rem',
              backgroundColor: '#dc2626',
              color: 'white',
              fontWeight: '500',
              padding: '0.5rem 0.75rem',
              borderRadius: '0.5rem',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              fontSize: '0.875rem',
            }}
            onClick={handleDelete}
            onMouseEnter={(e) => (e.target.style.backgroundColor = '#b91c1c')}
            onMouseLeave={(e) => (e.target.style.backgroundColor = '#dc2626')}
            disabled={isDeleting}
          >
            <Trash2 style={{ width: '0.875rem', height: '0.875rem' }} />
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', borderBottom: '1px solid #e0e0e0' }}>
        <div style={{ width: 'fit-content', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '0.9rem', fontWeight: 550, color: '#111827', margin: 0 }}>
          Current Organization:&nbsp;
          <span style={{
            backgroundColor: 'var(--primary-50)',
            color: 'var(--primary-600)',
            padding: '2px 6px',
            borderRadius: '0.25rem',
            marginLeft: '4px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            maxWidth: '150px',
            display: 'inline-block',
          }} title={activeDetail?.organization?.name}>
            {activeDetail?.organization?.name}
          </span>
        </div>
        <button
          onClick={() => setShowReassignModal(!showReassignModal)}
          type="button"
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.375rem',
            backgroundColor: 'var(--primary-500)',
            color: 'white',
            fontWeight: '500',
            padding: '0.5rem 0.75rem',
            borderRadius: '0.5rem',
            border: 'none',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            fontSize: '0.875rem',
          }}
          onMouseEnter={(e) => (e.target.style.backgroundColor = 'var( --primary-600)')}
          onMouseLeave={(e) => (e.target.style.backgroundColor = 'var( --primary-500)')}
        >
          Reassign Organization
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', borderBottom: '1px solid #e0e0e0' }}>
        <div style={{ width: 'fit-content', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '0.9rem', fontWeight: 550, color: '#111827', margin: 0 }}>
          Have a chat with your agent to test & experience it (Beta)
        </div>
        <button
          type="button"
          onClick={() => setStartChat(!startChat)}
          className='flex mb-10'
          style={{
            width: '100%',
            backgroundColor: 'var( --primary-500)',
            color: 'white',
            fontWeight: '500',
            padding: '0.5rem 0.75rem',
            borderRadius: '0.5rem',
            border: 'none',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            fontSize: '0.875rem',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          onMouseEnter={(e) => (e.target.style.backgroundColor = 'var( --primary-600)')}
          onMouseLeave={(e) => (e.target.style.backgroundColor = 'var( --primary-500)')}
        >
          Test your prompts via chat
        </button>
      </div>

      {!fromOrg && <div className='flex' style={{ flexDirection: 'column', gap: '0.5rem' }}>
        <div style={{ width: 'fit-content', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '0.9rem', fontWeight: 550, color: '#111827', margin: 0 }}>
          Have a web call with your agent to test & experience it (Beta)
        </div>
        <button
          onClick={startWebCall ? stopCall : startCall}
          type="button"
          disabled={isStartWebCall}
          className='flex mb-10'
          style={{
            width: '100%',
            backgroundColor: startWebCall || isStartWebCall ? '#dc2626' : 'var( --primary-500)',
            color: 'white',
            fontWeight: '500',
            padding: '0.5rem 0.75rem',
            borderRadius: '0.5rem',
            border: 'none',
            cursor: isStartWebCall ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s',
            fontSize: '0.875rem',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          onMouseEnter={(e) => {
            if (!isStartWebCall) {
              e.target.style.backgroundColor = startWebCall || isStartWebCall ? 'red' : 'var( --primary-600)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isStartWebCall) {
              e.target.style.backgroundColor = startWebCall || isStartWebCall ? '#dc2626' : 'var( --primary-500)';
            }
          }}
        >
          {isStartWebCall ? (
            <>
              <Spinner />
              Connecting...
            </>
          ) : startWebCall ? (
            <>
              <PhoneOff style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
              End web call
            </>
          ) : (
            <>
              <Phone style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
              Start web call
            </>
          )}
        </button>
      </div>}

      {showDeleteModal && (
        <ConfirmDeleteModal
          handleConfirmDelete={handleConfirmDelete}
          handleCancelDelete={handleDelete}
        />
      )}

      {showReassignModal && (
        <ReassignOrganizationModal
          organizations={organizations}
          closeModal={() => setShowReassignModal(false)}
          handleCancelReassign={() => setShowReassignModal(false)}
        />
      )}

{startChat && (
        <ChatWidget
          onSendMessage={onSendMessage}
          onClose={() => {
            setStartChat(false);
            setCurrentSession(null);
            setChatMessages([]);
          }}
          messages={chatMessages}
        />
      )}

      {showVariableModal && (
        <WebCallVariableModal
          promptMessage={activeDetail?.details?.model?.messages?.[0]?.content || ''}
          firstMessage={activeDetail?.details?.firstMessage || ''}
          onSubmit={handleVariableSubmit}
          onCancel={() => setShowVariableModal(false)}
        />
      )}

    </div>
  );
}

RightPanel.propTypes = {
  assistantId: PropTypes.string,
  fromOrg: PropTypes.bool,
};

export default RightPanel;
